import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import HeroSection from "./components/HeroSection";
import JobGrid from "./components/JobGrid";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useFilterPersistence, type FilterState } from "@/hooks/useFilterPersistence";

const locations = [
  { value: "", label: "Tất cả địa điểm" },
  { value: "an giang", label: "An Giang" },
  { value: "bắc ninh", label: "Bắc Ninh" },
  { value: "cà mau", label: "Cà Mau" },
  { value: "cao bằng", label: "Cao Bằng" },
  { value: "đăk lawk", label: "Đắk Lắk" },
  { value: "điện biên", label: "Điện Biên" },
  { value: "đồng nai", label: "Đồng Nai" },
  { value: "đồng tháp", label: "Đồng Tháp" },
  { value: "gia lai", label: "Gia Lai" },
  { value: "hà tĩnh", label: "Hà Tĩnh" },
  { value: "hưng yên", label: "Hưng Yên" },
  { value: "khánh hòa", label: "Khánh Hòa" },
  { value: "lai châu", label: "Lai Châu" },
  { value: "lâm đồng", label: "Lâm Đồng" },
  { value: "lạng sơn", label: "Lạng Sơn" },
  { value: "lào cai", label: "Lào Cai" },
  { value: "nghệ an", label: "Nghệ An" },
  { value: "ninh bình", label: "Ninh Bình" },
  { value: "phú thọ", label: "Phú Thọ" },
  { value: "quảng ngãi", label: "Quảng Ngãi" },
  { value: "quang ninh", label: "Quảng Ninh" },
  { value: "quảng trị", label: "Quảng Trị" },
  { value: "sơn la", label: "Sơn La" },
  { value: "tây ninh", label: "Tây Ninh" },
  { value: "thái nguyên", label: "Thái Nguyên" },
  { value: "thanh hóa", label: "Thanh Hóa" },
  { value: "cần thơ", label: "Cần Thơ" },
  { value: "đà nẵng", label: "Đà Nẵng" },
  { value: "hà nội", label: "Hà Nội" },
  { value: "hải phòng", label: "Hải Phòng" },
  { value: "tp. hồ chí minh", label: "TP. Hồ Chí Minh" },
  { value: "huế", label: "Huế" },
  { value: "tuyên quang", label: "Tuyên Quang" },
  { value: "vĩnh long", label: "Vĩnh Long" },
];

export default function Home() {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [activeLocation, setActiveLocation] = useState("");
  const [jobType, setJobType] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const restoreDoneRef = useRef(false);

  const {
    initialFilter,
    initialized,
    presets,
    persistFilter,
    savePreset,
    deletePreset,
    findMatchingPreset,
    getFilterLabel,
    isFilterEmpty,
  } = useFilterPersistence();

  // Restore saved filters on first mount
  useEffect(() => {
    if (initialized && initialFilter && !restoreDoneRef.current) {
      restoreDoneRef.current = true;
      setSearchQuery(initialFilter.searchQuery);
      setActiveCategory(initialFilter.activeCategory);
      setActiveLocation(initialFilter.activeLocation);
      setJobType(initialFilter.jobType);
      setSortBy(initialFilter.sortBy);
    }
  }, [initialized, initialFilter]);

  // Build current filter state
  const currentFilters: FilterState = useMemo(
    () => ({
      searchQuery,
      activeCategory,
      activeLocation,
      jobType,
      sortBy,
    }),
    [searchQuery, activeCategory, activeLocation, jobType, sortBy],
  );

  // Auto-save (debounced) on every change — skip initial restore phase
  const isRestoringRef = useRef(true);
  useEffect(() => {
    if (!initialized) return;
    // Skip the first change (restore phase)
    if (isRestoringRef.current) {
      isRestoringRef.current = false;
      return;
    }
    persistFilter(currentFilters);
  }, [currentFilters, initialized, persistFilter]);

  const activeLocationLabel = useMemo(() => {
    const loc = locations.find((l) => l.value === activeLocation);
    return loc ? loc.label : activeLocation;
  }, [activeLocation]);

  const matchingPreset = useMemo(
    () => findMatchingPreset(currentFilters),
    [currentFilters, findMatchingPreset],
  );

  const handleClearAll = useCallback(() => {
    setSearchQuery("");
    setActiveCategory("");
    setActiveLocation("");
    setJobType("");
    setSortBy("newest");
  }, []);

  const handleApplyPreset = useCallback((filters: FilterState) => {
    setSearchQuery(filters.searchQuery);
    setActiveCategory(filters.activeCategory);
    setActiveLocation(filters.activeLocation);
    setJobType(filters.jobType);
    setSortBy(filters.sortBy);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <HeroSection
          searchQuery={searchQuery}
          activeCategory={activeCategory}
          activeLocation={activeLocation}
          onSearchChange={setSearchQuery}
          onCategoryChange={setActiveCategory}
          onLocationChange={setActiveLocation}
        />
        <JobGrid
          searchQuery={searchQuery}
          activeCategory={activeCategory}
          activeLocation={activeLocation}
          activeLocationLabel={activeLocationLabel}
          jobType={jobType}
          sortBy={sortBy}
          onCategoryChange={setActiveCategory}
          onSearchChange={setSearchQuery}
          onLocationChange={setActiveLocation}
          onJobTypeChange={setJobType}
          onSortChange={setSortBy}
          onClearAll={handleClearAll}
          currentFilters={currentFilters}
          presets={presets}
          matchingPreset={matchingPreset}
          isFilterEmpty={isFilterEmpty}
          getFilterLabel={getFilterLabel}
          onSavePreset={savePreset}
          onDeletePreset={deletePreset}
          onApplyPreset={handleApplyPreset}
        />
      </main>
      <Footer />
    </div>
  );
}